import { tracked } from '@glimmer/tracking';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { htmlSafe } from '@ember/template';
import PhSidebarSimple from 'ember-phosphor-icons/components/ph-sidebar-simple';
import { t } from 'ember-intl';
import type IntlService from 'ember-intl/services/intl';
import Modal from 'tio-ui/components/modal';
import TooltipButton from 'tio-ui/components/tooltip-button';
import Footer from 'tio-ui/components/footer';
import Input from 'tio-ui/components/input';
import Textarea from 'tio-ui/components/textarea';
import { VStack } from 'tio-ui/components/layout';
import AlertBanner from 'tio-ui/components/alert-banner';
import { not, or } from 'tio-ui/utilities';
import { dropTask } from 'ember-concurrency';
import { divide } from 'tio-ui/math';
import inputmask from '../../../modifiers/inputmask.ts';
import { getInputmaskUnmaskedValue } from '../../../utils/inputmask.ts';
import safeFormatNumber from '../../../helpers/safe-format-number';
import LimitsSummary from '../../tuition-assistance/limits-summary.gts';
import SelectTextArea from '../../tio/select-text-area.gts';
import TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklist from '../../tuition-assistance/application-details/courses-approval-waiver-checklist.gts';
import ConfirmationDialog from 'tio-ui/components/confirmation-dialog';
import ErrorMessages from 'tio-ui/components/error-messages';
import {
  buildTemplateResponseOptions,
  getSingleValueForTasField,
  setTrackedTasFieldValue,
} from '../../../utils/tuition-assistance/fields.ts';
import { doEmployeesExistInApproverList } from '../../../utils/tuition-assistance/approvers.ts';
import CalculatePrePercentageValue from '../../../utils/tuition-assistance/calculate-pre-percentage-value.ts';
import type Owner from '@ember/owner';
import type { Store } from '@warp-drive/core';
import type RouterService from '@ember/routing/router-service';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type TuitionAssistanceService from '../../../services/tuition-assistance';
import type FeaturesService from '../../../services/features';
import type { NotificationsService } from 'tio-ui/components/notifications';

import type SessionContextService from '../../../services/session-context.ts';
import { findRecord } from '@warp-drive/utilities/json-api';
import { saveRecord } from 'tio-data/requests/save-record';
import { reportSeveredLink } from 'tio-data/utils/severed-link-report';
import {
  waiveEvidenceApprovalRequest,
  updateApprovedTotalRequest,
  approveCoursesRequest,
  approveEvidenceRequest,
  type TasApplicationTransitionFieldData,
} from 'tio-data/requests/tas-application-actions';
import { buildTasActorFromSession } from 'tio-data/utils/tas-actor';
/**
 * Approval Modal Component
 *
 * Focused modal for approving TAS applications. Handles:
 * - Course approval (PENDING_COURSES_APPROVAL)
 * - Evidence approval (PENDING_EVIDENCE_APPROVAL)
 * - Approved amount editing with validation
 * - Course Cash dual totals for prepaid programs
 * - Limits validation integration
 * - Waiver checklist for skipping evidence requirements
 * - Auto-evidence approval trigger
 *
 * Triggered from: approval-page > approval-footer > Approve button
 */
interface ApproveModalSignature {
  Element: HTMLDivElement;
  Args: {
    /** Whether modal is open */
    show: boolean;
    /** Callback to close modal */
    onClose: () => void;
    /** TAS Application to approve */
    application: TasApplicationModel;
    /** Persisted comment draft supplied by the footer */
    draftComment: string;
    /** Propagate comment draft changes back to the footer */
    onDraftCommentChange: (value: string) => void;
  };
}

class ApproveModal extends Component<ApproveModalSignature> {
  @service declare store: Store;
  @service declare tuitionAssistance: TuitionAssistanceService;
  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare features: FeaturesService;
  @service declare notifications: NotificationsService;
  @service declare intl: IntlService;

  // Form state
  @tracked approvedTotal = 0;
  @tracked cardFinalApprovedTotal = 0;
  @tracked comment = '';
  @tracked submissionError: string | null = null;

  // Approver-instructions side panel. Closed on open so the modal keeps its
  // usual footprint until the approver asks for the instructions.
  @tracked isInstructionsOpen = false;

  // Pre-percentage values (for percentage-based calculations)
  @tracked prePercentageTotal = 0;
  @tracked prePercentageCourseCashTotal = 0;

  // Seed values for editable currency inputs. Bound as @value on open/reopen only —
  // never updated during typing. This prevents the reimagined Input (a controlled component)
  // from re-setting the DOM value mid-keystroke, which causes inputmask to re-apply and steal focus.
  // The financial-details-request-summary pattern (no @value, read unmaskedvalue() only) would
  // also work but loses pre-population from existing application totals.
  @tracked approvedTotalInputValue = '0';
  @tracked cardFinalApprovedTotalInputValue = '0';
  @tracked prePercentageTotalInputValue = '0';
  @tracked prePercentageCourseCashTotalInputValue = '0';

  // Waiver state
  @tracked hasConfirmedEvidenceAttachmentWaiver = false;
  @tracked hasConfirmedEvidenceApprovalWaiver = false;
  @tracked hasRespondedToAllChecklistItems = false;

  // UI state
  @tracked isProcessing = false;
  @tracked showConfirmApproverOverridePrompt = false;

  constructor(owner: Owner, args: ApproveModalSignature['Args']) {
    super(owner, args);
    // Initialize on first load if modal is shown
    if (args.show) {
      this.initializeFormDefaults();
    }
  }

  /**
   * Optional, admin-authored HTML rendered at the top of the modal (configured
   * per program template in the builder's "Approver inputs" section, e.g. a
   * reference table the approver should see). Returns the raw string (or null
   * when unset) — it's rendered into a sandboxed <iframe srcdoc> below so its
   * styles/scripts stay isolated and can't restyle the approval page.
   */
  get headerHtml() {
    const fields = this.args.application.tasProgramInstance?.tasProgramTemplate?.fields;
    if (!fields) {
      return null;
    }
    const value = getSingleValueForTasField('APPROVAL_MODAL_HEADER_HTML', fields);
    return typeof value === 'string' && value.trim() !== '' ? value : null;
  }

  /**
   * Widen the modal when admin-authored header HTML is present so reference
   * tables / notes have room to breathe; otherwise keep the default width.
   */
  get modalSize() {
    return this.headerHtml ? '4xl' : 'xl';
  }

  /**
   * Program-configured approver instructions, ordinal-sorted by the resource
   * getter. Also surfaced in the Employer Info drawer — but that drawer is
   * behind the modal once approving starts, which is what this panel fixes.
   */
  get approverInstructions() {
    const instructions =
      this.args.application.tasProgramInstance?.tasProgramTemplate?.programApproverInstructions ??
      [];
    // The unheaded fallback is user-facing, so it uses the already-translated
    // `instruction` key rather than a hardcoded English word.
    const instructionWord = this.intl.t('tuition_assistance.application_details.instruction');
    return instructions.map((instruction) => ({
      heading: instruction.heading || `${instructionWord} ${instruction.ordinal}`,
      body: instruction.body,
      ordinal: instruction.ordinal,
    }));
  }

  get hasApproverInstructions() {
    return this.approverInstructions.length > 0;
  }

  toggleInstructions = () => {
    this.isInstructionsOpen = !this.isInstructionsOpen;
  };

  /**
   * Initialize form with default values when modal opens
   * Call this whenever the modal is shown to reset state
   */
  initializeFormDefaults() {
    // TODO: Auto-populate approved total based on calculation strategy
    // For now, initialize with requested amount or existing approved amount
    const { application } = this.args;
    const reimbursementPercentage = this.reimbursementPercentage;

    // Initialize Course Cash amount if applicable
    if (this.isCourseCash) {
      // Use cardFinalApprovedTotal if it exists, otherwise fall back to cardPreApprovedTotal
      const cardTotal = application.cardFinalApprovedTotal ?? application.cardPreApprovedTotal ?? 0;
      this.cardFinalApprovedTotal = typeof cardTotal === 'number' ? cardTotal : 0;

      // For Course Cash applications, "All Other Expenses" should start at 0
      // since the course cash total is tracked separately
      const existingApprovedTotal = application.approvedTotal || 0;
      this.approvedTotal = existingApprovedTotal > 0 ? existingApprovedTotal : 0;

      // Initialize pre-percentage values for percentage-based calculations
      if (
        this.isCalculatedApplicationTotal &&
        reimbursementPercentage &&
        reimbursementPercentage > 0
      ) {
        this.prePercentageCourseCashTotal = CalculatePrePercentageValue(
          this.cardFinalApprovedTotal,
          reimbursementPercentage
        );
        this.prePercentageTotal = CalculatePrePercentageValue(
          this.approvedTotal,
          reimbursementPercentage
        );
      }
    } else {
      // For non-Course Cash applications, use approved total or requested total
      const existingApprovedTotal = application.approvedTotal || 0;
      this.approvedTotal =
        existingApprovedTotal > 0 ? existingApprovedTotal : application.requestedTotal || 0;

      // Initialize pre-percentage value for percentage-based calculations
      if (this.isCalculatedApplicationTotal && reimbursementPercentage) {
        this.prePercentageTotal = CalculatePrePercentageValue(
          this.approvedTotal,
          reimbursementPercentage
        );
      }
    }

    // Sync display strings for currency inputs (set once — not updated during typing)
    this.approvedTotalInputValue = String(this.approvedTotal / 100);
    this.cardFinalApprovedTotalInputValue = String(this.cardFinalApprovedTotal / 100);
    this.prePercentageTotalInputValue = String(this.prePercentageTotal / 100);
    this.prePercentageCourseCashTotalInputValue = String(this.prePercentageCourseCashTotal / 100);

    // Reset other form state
    this.comment = this.args.draftComment || this.templateApproveComment;
    this.submissionError = null;
    this.hasConfirmedEvidenceAttachmentWaiver = false;
    this.hasConfirmedEvidenceApprovalWaiver = false;
  }

  /**
   * Reset form state to defaults
   */
  resetFormState() {
    this.approvedTotal = 0;
    this.cardFinalApprovedTotal = 0;
    this.prePercentageTotal = 0;
    this.prePercentageCourseCashTotal = 0;
    this.approvedTotalInputValue = '0';
    this.cardFinalApprovedTotalInputValue = '0';
    this.prePercentageTotalInputValue = '0';
    this.prePercentageCourseCashTotalInputValue = '0';
    this.comment = this.args.draftComment;
    this.submissionError = null;
    this.hasConfirmedEvidenceAttachmentWaiver = false;
    this.hasConfirmedEvidenceApprovalWaiver = false;
    this.hasRespondedToAllChecklistItems = false;
    this.showConfirmApproverOverridePrompt = false;
    this.isProcessing = false;
  }

  /**
   * Determine if application is in PENDING_COURSES_APPROVAL state
   */
  get isPendingCoursesApproval() {
    return this.args.application.state === 'TAS.ApplicationState.PENDING_COURSES_APPROVAL';
  }

  /**
   * Determine if application is in PENDING_EVIDENCE_APPROVAL state
   */
  get isPendingEvidenceApproval() {
    return this.args.application.state === 'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL';
  }

  /**
   * For pre-paid (Direct Pay) applications in evidence review, payment has already
   * been made. Limits validation is misleading since the annual usage already
   * includes this application's payment amount.
   */
  get isPostPaymentReview() {
    return this.isPendingEvidenceApproval && (this.args.application.amounts?.paid ?? 0) > 0;
  }

  /**
   * Check if application uses Course Cash payment method
   */
  get isCourseCash() {
    return this.args.application.paymentMethod === 'TAS.AssistanceManner.COURSE_CASH';
  }

  get isVoucherProgram() {
    return !!this.args.application.tasProgramInstance?.tasProgramTemplate?.isVoucherProgram;
  }

  /**
   * Check if application uses credit-based limits instead of monetary
   */
  get isCreditBased() {
    return this.args.application.isCreditBased;
  }

  /**
   * Check if application uses percentage-based or GPA-based calculation
   * This determines whether to show the "X% reimbursement of" inputs
   */
  get isCalculatedApplicationTotal() {
    const template = this.args.application.tasProgramInstance?.tasProgramTemplate;
    if (!template) return false;
    return template.calculateTotalByPercent || template.calculateTotalByGPA;
  }

  /**
   * Get the reimbursement percentage from the program template
   */
  get reimbursementPercentage() {
    return (
      this.args.application.tasProgramInstance?.tasProgramTemplate?.reimbursementPercentage ?? 0
    );
  }

  /**
   * Per-course expense rows behind the requested amount, shown read-only in the modal.
   * The modal is a focus-trapping overlay, so an approver confirming what to type into
   * the "X% reimbursement of" input can't read the expenses off the page underneath —
   * they had to cancel out and reopen (SUP-7638). `courseTotal` is used so credit-rate
   * programs show the same figure that feeds `requestedTotalAllCourses`.
   */
  get requestedExpenseRows() {
    // Course Cash splits the approver's inputs into card tuition and everything else, so a
    // single tuition-inclusive total lines up with neither and reads as a third figure.
    // Gated on isCourseCash rather than showCourseCashField so it also stays hidden during
    // evidence approval, where the lone reimbursement input likewise excludes card tuition.
    if (this.isCourseCash) {
      return [];
    }

    const rows = this.args.application.activeCourses.map((course, index) => {
      const number = String(course.courseNumber ?? '').trim();
      const name = String(course.courseName ?? '').trim();

      return {
        label:
          [number, name].filter(Boolean).join(' — ') ||
          this.intl.t('common.tas.approval.approve_modal.requested_expenses.course_fallback', {
            number: index + 1,
          }),
        amount: course.courseTotal,
      };
    });

    const { scholarshipsTotal } = this.args.application;

    // Only ever a deduction from course rows — on its own it would render a breakdown
    // with a $0 total, which is worse than showing nothing.
    if (rows.length && scholarshipsTotal > 0) {
      rows.push({
        label: this.intl.t('common.tas.approval.approve_modal.requested_expenses.scholarships'),
        amount: -scholarshipsTotal,
      });
    }

    return rows;
  }

  /**
   * Total expenses requested, before any reimbursement percentage is applied — i.e. the
   * figure the "X% reimbursement of" input expects.
   */
  get requestedExpensesTotal() {
    const { requestedTotalAllCourses, scholarshipsTotal } = this.args.application;
    return Math.max(0, requestedTotalAllCourses - scholarshipsTotal);
  }

  /**
   * Get response template field name based on current state
   */
  get responseTemplateFieldName() {
    if (this.isPendingCoursesApproval) {
      return 'COURSES_APPROVAL_APPROVE_TEXT';
    }

    if (this.isPendingEvidenceApproval) {
      return 'EVIDENCE_APPROVAL_APPROVE_TEXT';
    }

    return null;
  }

  /**
   * Get the TAS response tag for the current approval action.
   */
  private getTagForApproval(): string | null {
    if (this.isPendingCoursesApproval) return 'TAS.CourseApproval.APPROVE';
    if (this.isPendingEvidenceApproval) return 'TAS.EvidenceApproval.APPROVE';
    return null;
  }

  /**
   * Get company comments filtered by a specific TAS response tag.
   */
  private companyCommentsForTag(tag: string): { title: string; body: string }[] {
    const partnerComments =
      this.args.application.tasProgramInstance?.employee?.company?.comments ?? [];
    return partnerComments
      .filter((comment) => comment.tags?.includes(tag))
      .map((comment) => comment.tasApprovalNote);
  }

  /**
   * The single source of truth for the approve comment picker: the program
   * template's response templates (multi-option TasFieldOptions) OR its single
   * inline `fields` note, merged with company canned responses tagged for this
   * action. Both `templateApproveComment` (pre-fill) and `approveTextAreaOptions`
   * (dropdown) derive from this, so a lone company comment can never hide the
   * program note (see buildTemplateResponseOptions).
   */
  get combinedApproveOptions(): { title: string; body: string }[] {
    const template = this.args.application.tasProgramInstance?.tasProgramTemplate;
    const templateOptions = buildTemplateResponseOptions(
      this.responseTemplateFieldName,
      template?.fields,
      template?.tasFieldOptions ?? [],
      this.intl.t('common.tas.approval.program_response_label')
    );
    const tag = this.getTagForApproval();
    const companyOptions = tag ? this.companyCommentsForTag(tag) : [];

    return [...templateOptions, ...companyOptions];
  }

  /**
   * Get template text for approve comment.
   * When exactly 1 combined option is configured, pre-fill the textarea with it.
   */
  get templateApproveComment() {
    const allOptions = this.combinedApproveOptions;
    return allOptions.length === 1 ? (allOptions[0]?.body ?? '') : '';
  }

  /**
   * Convert combined response options to SelectTextArea format.
   * 2+ options → dropdown; 0–1 → plain textarea (a single option pre-fills via
   * templateApproveComment). Options are scoped to the approve action (approve
   * field text + APPROVE-tagged company comments), so reject / request-change
   * responses never appear in the approve modal.
   */
  get approveTextAreaOptions() {
    const allOptions = this.combinedApproveOptions;
    return allOptions.length > 1 ? allOptions : [];
  }

  /**
   * Determine which field to update (PRE_APPROVED_TOTAL or FINAL_APPROVED_TOTAL)
   */
  get fieldToUpdate(): 'PRE_APPROVED_TOTAL' | 'FINAL_APPROVED_TOTAL' {
    // Before pre-approval states use PRE_APPROVED_TOTAL
    const beforePreApprovalStates = [
      'TAS.ApplicationState.CREATED',
      'TAS.ApplicationState.DEFINE_COURSES',
      'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
      'TAS.ApplicationState.COURSES_APPROVED',
      'TAS.ApplicationState.COURSES_NOT_APPROVED',
      'TAS.ApplicationState.COURSES_REJECTED',
    ];

    return beforePreApprovalStates.includes(this.args.application.state)
      ? 'PRE_APPROVED_TOTAL'
      : 'FINAL_APPROVED_TOTAL';
  }

  /**
   * Check if Course Cash field should be shown
   * Only show for Course Cash programs in PENDING_COURSES_APPROVAL
   */
  get showCourseCashField() {
    return this.isCourseCash && this.isPendingCoursesApproval;
  }

  /**
   * Check if waiver checklist should be displayed
   * Only show for PENDING_COURSES_APPROVAL when waiver is allowed
   */
  get canSkipFinalApprovalOrAttachmentsWithChecklist() {
    const template = this.args.application.tasProgramInstance?.tasProgramTemplate;
    if (!template) {
      reportSeveredLink({
        observer: 'approve-modal',
        edge: this.args.application.tasProgramInstance
          ? 'tasProgramInstance.tasProgramTemplate'
          : 'tasApplication.tasProgramInstance',
        recordId: this.args.application.id ?? undefined,
      });
      // The waiver flag is unreadable, so degrade to the standard no-skip approval flow.
      return false;
    }
    return (
      this.isPendingCoursesApproval &&
      getSingleValueForTasField('ALLOW_WAIVER_OF_EVIDENCE_APPROVAL', template.fields) === true
    );
  }

  /**
   * Check if current user is the expected approver
   * Used to show out-of-sequence warning
   */
  get isExpectedApprover() {
    const expectedApproverEmployeeIds = this.args.application.currentExpectedApproverEmployeeIds;
    const employees = this.sessionContext.user?.person?.employees || [];
    return doEmployeesExistInApproverList(employees, expectedApproverEmployeeIds);
  }

  /**
   * Check if employee has applications in repayment status
   */
  get employeeHasAppsInRepayment() {
    return !!this.appsInRepayment.length;
  }

  /**
   * Get list of application IDs that are in repayment
   */
  get appsInRepayment() {
    // This would need to be fetched from the employee's applications
    // For now, return empty array - implementation depends on data availability
    return [];
  }

  /**
   * Whether the approval has a valid total amount.
   * For voucher programs the voucher value is set separately (VOUCHER_AMOUNT), so a
   * $0 reimbursement total is valid in both courses AND evidence approval (SUP-7555).
   * Negative totals are still rejected here, matching the API (which rejects them too).
   * For Course Cash apps in courses approval, only card total matters.
   * For Course Cash apps in evidence approval, either card or reimbursement total suffices.
   * For reimbursement apps, only reimbursement total matters.
   */
  get hasValidTotal() {
    if (this.isVoucherProgram) {
      return this.approvedTotal >= 0;
    }
    if (this.showCourseCashField) {
      return this.cardFinalApprovedTotal > 0;
    }
    if (this.isCourseCash) {
      return this.approvedTotal > 0 || this.cardFinalApprovedTotal > 0;
    }
    return this.approvedTotal > 0;
  }

  /**
   * Build a list of human-readable reasons the Approve button is currently blocked.
   * Does not include the processing guard so it remains accurate during submission.
   * Limits validation (Summary.isDisabledSubmitForApprover) is handled in the template
   * because it comes from the LimitsSummary block scope.
   */
  get validationBlockersForDisplay(): string[] {
    const blockers: string[] = [];

    if (!this.isPostPaymentReview) {
      if (!this.hasValidTotal) {
        blockers.push(
          this.intl.t('common.tas.approval.approve_modal.validation_blockers.amount_required')
        );
      }
    }

    if (!this.comment) {
      blockers.push(
        this.intl.t('common.tas.approval.approve_modal.validation_blockers.comment_required')
      );
    }

    if (
      this.canSkipFinalApprovalOrAttachmentsWithChecklist &&
      !this.hasRespondedToAllChecklistItems
    ) {
      blockers.push(
        this.intl.t(
          'common.tas.approval.approve_modal.validation_blockers.waiver_checklist_required'
        )
      );
    }

    return blockers;
  }

  get hasValidationBlockers() {
    return this.validationBlockersForDisplay.length > 0;
  }

  /**
   * Get the appropriate route for returning to the approval queue
   * Routes differ based on consuming app
   */
  get approvalQueueRoute() {
    if (this.features.isEmployeeApp) {
      return 'authenticated.admin.tas.approval-queue';
    }
    // Default for tio-admin
    return 'authenticated.tas.approval-queue';
  }

  /**
   * Check if TAS participant is inactive
   */
  get isTasParticipantInactive() {
    const programInstance = this.args.application.tasProgramInstance;
    if (!programInstance) {
      reportSeveredLink({
        observer: 'approve-modal',
        edge: 'tasApplication.tasProgramInstance',
        recordId: this.args.application.id ?? undefined,
      });
      // Activity is unreadable; never assert "inactive" from a severed link.
      return false;
    }
    return !programInstance.employee?.tasParticipant?.isActive;
  }

  /**
   * Validate if approval can be submitted
   */
  get canSubmitApproval() {
    // For post-payment evidence reviews, only require a comment
    if (this.isPostPaymentReview) {
      return !this.isProcessing && this.comment.length > 0;
    }

    // If waiver checklist is shown, require all items to be responded to
    if (this.canSkipFinalApprovalOrAttachmentsWithChecklist) {
      return (
        !this.isProcessing &&
        this.hasValidTotal &&
        this.comment.length > 0 &&
        this.hasRespondedToAllChecklistItems
      );
    }

    return (
      !this.isProcessing && this.hasValidTotal && this.comment.length > 0
      // Limits validation is handled by LimitsSummary component
      // which will disable the button via isDisabledSubmitForApprover
    );
  }

  /**
   * Update approved total input (direct input, not percentage-based)
   * Uses inputmask.unmaskedvalue() because input.value returns the masked string
   */
  updateApprovedTotal = (_maskedValue: unknown, event?: InputEvent) => {
    const unmaskedValue = getInputmaskUnmaskedValue(event) ?? '0';
    this.approvedTotal = Math.round(Number(unmaskedValue) * 100);
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  /**
   * Update Course Cash total input (direct input, not percentage-based)
   */
  updateCardFinalApprovedTotal = (_maskedValue: unknown, event?: InputEvent) => {
    const unmaskedValue = getInputmaskUnmaskedValue(event) ?? '0';
    this.cardFinalApprovedTotal = Math.round(Number(unmaskedValue) * 100);
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  /**
   * Update approved total via pre-percentage input (percentage-based calculation)
   * Takes the pre-percentage amount and calculates the final approved amount
   */
  updateApprovedTotalFromPrePercentage = (_maskedValue: unknown, event?: InputEvent) => {
    const unmaskedValue = getInputmaskUnmaskedValue(event) ?? '0';
    const numericValue = Number(unmaskedValue);
    const reimbursementPercentage = this.reimbursementPercentage;

    if (isNaN(numericValue) || !reimbursementPercentage || isNaN(reimbursementPercentage)) {
      this.approvedTotal = 0;
      this.prePercentageTotal = 0;
      return;
    }

    const updatedPrePercentageTotal = Math.round(numericValue * 100);
    this.prePercentageTotal = updatedPrePercentageTotal;
    this.approvedTotal = Math.round(updatedPrePercentageTotal * (reimbursementPercentage / 100));
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  /**
   * Update Course Cash total via pre-percentage input (percentage-based calculation)
   */
  updateCourseCashFromPrePercentage = (_maskedValue: unknown, event?: InputEvent) => {
    const unmaskedValue = getInputmaskUnmaskedValue(event) ?? '0';
    const numericValue = Number(unmaskedValue);
    const reimbursementPercentage = this.reimbursementPercentage;

    if (isNaN(numericValue) || !reimbursementPercentage || isNaN(reimbursementPercentage)) {
      this.cardFinalApprovedTotal = 0;
      this.prePercentageCourseCashTotal = 0;
      return;
    }

    const updatedPrePercentageTotal = Math.round(numericValue * 100);
    this.prePercentageCourseCashTotal = updatedPrePercentageTotal;
    this.cardFinalApprovedTotal = Math.round(
      updatedPrePercentageTotal * (reimbursementPercentage / 100)
    );
    this.tuitionAssistance.refreshActiveLimitsSummaryComponents();
  };

  /**
   * Update comment textarea
   */
  updateComment = (value: string) => {
    this.comment = value;
    this.args.onDraftCommentChange(value);
  };

  /**
   * Handle waiver checklist changes
   */
  didChangeApprovalWaiverChecklist = ({
    hasConfirmedEvidenceApprovalWaiver,
    hasConfirmedEvidenceAttachmentWaiver,
    hasRespondedToAllChecklistItems,
  }: {
    hasConfirmedEvidenceApprovalWaiver: boolean;
    hasConfirmedEvidenceAttachmentWaiver: boolean;
    hasRespondedToAllChecklistItems: boolean;
  }) => {
    this.hasConfirmedEvidenceApprovalWaiver = hasConfirmedEvidenceApprovalWaiver;
    this.hasConfirmedEvidenceAttachmentWaiver = hasConfirmedEvidenceAttachmentWaiver;
    this.hasRespondedToAllChecklistItems = hasRespondedToAllChecklistItems;
  };

  /**
   * Handle modal close
   */
  handleClose = () => {
    if (!this.isProcessing) {
      this.args.onClose();
    }
  };

  /**
   * Apply evidence waivers if applicable
   * This sets flags to skip evidence requirements based on waiver checklist
   */
  waiveRequirementsIfApplicable = async (application: TasApplicationModel) => {
    if (
      !this.canSkipFinalApprovalOrAttachmentsWithChecklist ||
      // Evidence approval can only be waived if attachment requirement is waived also
      !this.hasConfirmedEvidenceAttachmentWaiver
    ) {
      return;
    }

    if (this.hasConfirmedEvidenceApprovalWaiver) {
      // This endpoint sets WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT and
      // WAIVE_EVIDENCE_APPROVAL_REQUIREMENT to true, overriding approval summaries
      // to enable skipping subsequent approval steps.
      return this.store.request(waiveEvidenceApprovalRequest(application.id!));
    } else {
      // If not waiving full approval, still waive the attachment requirement
      setTrackedTasFieldValue(application, 'WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT', true);
      // WarpDrive mutation (Phase 2.7) — replaces application.save().
      return saveRecord(this.store, application);
    }
  };

  /**
   * Attempt to submit approval - checks if user is expected approver first
   */
  attemptSubmitApproval = () => {
    if (!this.isExpectedApprover) {
      this.showConfirmApproverOverridePrompt = true;
    } else {
      this.submitApprovalTask.perform();
    }
  };

  /**
   * Cancel the approver override prompt
   */
  cancelApproverOverride = () => {
    this.showConfirmApproverOverridePrompt = false;
  };

  /**
   * Main approval submission task
   */
  submitApprovalTask = dropTask(async () => {
    this.showConfirmApproverOverridePrompt = false;
    this.isProcessing = true;
    this.submissionError = null;

    try {
      const { application } = this.args;

      // Step 1: Update approved total(s) — skip for post-payment evidence reviews.
      if (!this.isPostPaymentReview) {
        const additionalFields: Record<string, unknown> = {};

        // For Course Cash programs, include card total
        if (this.showCourseCashField) {
          additionalFields['CARD_FINAL_APPROVED_TOTAL'] = this.cardFinalApprovedTotal;
        }

        await this.store.request(
          updateApprovedTotalRequest(
            application.id!,
            this.approvedTotal,
            this.comment,
            additionalFields
          )
        );
        // Reload to pick up the updated approved total and tas-assistances.
        // WarpDrive builder read — replaces tasApplication.reload({ include: 'tas-assistances' }).
        const { content: reloadedContent } = (await this.store.request(
          findRecord<TasApplicationModel>('tas-application', application.id!, {
            include: 'tas-assistances',
            reload: true,
          })
        )) as { content: { data: TasApplicationModel } };
        void reloadedContent; // result unused — reload updates the store cache in place
      }

      // Step 2: Set VOUCHER_AMOUNT for voucher programs (courses and evidence approval).
      if (this.isVoucherProgram) {
        const employee = application.tasProgramInstance?.employee;
        // Fetch limits fresh at submit time to guarantee we have the actual available amount
        // (limitsData may still be loading or stale from the LimitsSummary component)
        const limitsData = employee
          ? await this.tuitionAssistance.getV2LimitsStatus(employee, application)
          : this.tuitionAssistance.limitsData;
        const voucherAmount = limitsData?.available ?? 0;
        setTrackedTasFieldValue(application, 'VOUCHER_AMOUNT', voucherAmount);
        // WarpDrive mutation (Phase 2.7) — replaces application.save().
        await saveRecord(this.store, application);
      }

      // Step 3: Apply waivers if attachment waiver is confirmed
      if (this.hasConfirmedEvidenceAttachmentWaiver) {
        await this.waiveRequirementsIfApplicable(application);
      }

      // Step 4: Call main approval state-transition.
      const actor = buildTasActorFromSession(this.sessionContext);
      const fieldData: TasApplicationTransitionFieldData = {
        preApprovedTotal:
          (getSingleValueForTasField('PRE_APPROVED_TOTAL', application.fields) as number | null) ??
          null,
        finalApprovedTotal:
          (getSingleValueForTasField('FINAL_APPROVED_TOTAL', application.fields) as
            number | null) ?? null,
        waiveEvidenceAttachmentRequirement:
          (getSingleValueForTasField(
            'WAIVE_EVIDENCE_ATTACHMENT_REQUIREMENT',
            application.fields
          ) as boolean | null) ?? false,
        waiveEvidenceApprovalRequirement:
          (getSingleValueForTasField('WAIVE_EVIDENCE_APPROVAL_REQUIREMENT', application.fields) as
            boolean | null) ?? false,
      };

      if (this.isPendingCoursesApproval) {
        await this.store.request(
          approveCoursesRequest(application.id!, actor, this.comment, fieldData)
        );
      } else {
        await this.store.request(
          approveEvidenceRequest(application.id!, actor, this.comment, fieldData)
        );
      }
      // Reload to pick up state changes written by the transition.
      // WarpDrive builder read — replaces tasApplication.reload() in triggerActionAndReload.
      await this.store.request(
        findRecord<TasApplicationModel>('tas-application', application.id!, { reload: true })
      );

      this.args.onDraftCommentChange('');

      // Step 5: Trigger auto-evidence if full approval waiver checked
      if (this.hasConfirmedEvidenceApprovalWaiver) {
        await this.tuitionAssistance.triggerAutoEvidenceApprovalForApplication(
          application,
          this.comment
        );
      }

      // Show success notification
      const applicantName =
        application.tasProgramInstance?.employee?.person?.fullName ?? 'Applicant';
      const programName =
        application.tasProgramInstance?.tasProgramTemplate?.programName ?? 'program';
      this.notifications.add(`Application approved for ${applicantName} — ${programName}`, {
        appearance: 'success',
      });

      // Navigate back to approval queue
      this.router.transitionTo(this.approvalQueueRoute);
    } catch (error) {
      console.error('Approval submission failed:', error);
      this.submissionError =
        error instanceof Error ? error.message : 'An error occurred during approval';
    } finally {
      this.isProcessing = false;
    }
  });

  <template>
    <LimitsSummary
      @application={{@application}}
      @employee={{@application.tasProgramInstance.employee}}
      @approvedTotalToValidate={{if
        this.isCreditBased
        @application.totalCredits
        this.approvedTotal
      }}
      @approvedCourseCashTotalToValidate={{if
        this.showCourseCashField
        this.cardFinalApprovedTotal
        0
      }}
      @ignoreLimitsValidation={{this.isPostPaymentReview}}
      @shouldFetchLimits={{true}}
      as |Summary|
    >
      <Modal
        @isOpen={{@show}}
        @onClose={{this.handleClose}}
        @size={{this.modalSize}}
        @isAsideOpen={{this.isInstructionsOpen}}
        @asideSize="sm"
        @asideLabel={{t "tuition_assistance.application_approval.approver_instructions"}}
        data-test-approve-modal
      >
        <:header>
          {{! pr-6 keeps the toggle clear of the modal's own close button }}
          <div class="tio-row-between gap-4 pr-6">
            <div>
              <div>Approve Application</div>
              <div class="tio-text-sm-normal tio-fg-muted mt-0.5">
                {{@application.tasProgramInstance.employee.person.fullName}}
                —
                {{@application.tasProgramInstance.tasProgramTemplate.programName}}
              </div>
            </div>
            {{#if this.hasApproverInstructions}}
              {{! Icon-only drawer toggle, matching the toolbar pattern the
              approval footer already uses: minimal appearance so it carries no
              chrome when off, @isActive tinting it while the drawer is out, and
              the hover tooltip naming the action. This is the drawer's only
              control — the drawer itself has no close button. }}
              <TooltipButton
                @tooltip={{if
                  this.isInstructionsOpen
                  (t "common.tas.approval.approve_modal.instructions_hide")
                  (t "common.tas.approval.approve_modal.instructions_show")
                }}
                @tooltipPlacement="top"
                @intent="default"
                @appearance="minimal"
                @size="sm"
                @icon={{PhSidebarSimple}}
                @isActive={{this.isInstructionsOpen}}
                aria-expanded={{if this.isInstructionsOpen "true" "false"}}
                {{on "click" this.toggleInstructions}}
                data-test-approve-modal-instructions-toggle
              />
            {{/if}}
          </div>
        </:header>

        <:body>
          <VStack>
            {{! Program-configured header HTML (APPROVAL_MODAL_HEADER_HTML) — trusted,
            admin-authored content shown at the top of the modal, in a fully
            sandboxed iframe (empty sandbox = no scripts, no same-origin) so its
            styles can't escape/restyle the page and any stray script can't run.
            The header use case is reference tables / notes — no scripting needed. }}
            {{#if this.headerHtml}}
              <iframe
                title="Program information"
                srcdoc={{this.headerHtml}}
                sandbox=""
                class="block h-[60vh] w-full rounded-lg border tio-border-default tio-bg-surface"
                data-test-approval-header-html
              ></iframe>
            {{/if}}

            {{! Error Display }}
            {{#if this.submissionError}}
              <AlertBanner @intent="warning" data-test-approve-modal-error>
                {{this.submissionError}}
              </AlertBanner>
            {{/if}}

            {{! Limits validation alert }}
            <Summary.LimitsAlertForApproverIfApplicable class="text-sm flex" />

            {{! Remaining Benefit Summary }}
            {{#unless this.isPostPaymentReview}}
              <div
                class="app-status-panel app-status-panel--brand p-4 space-y-2"
                data-test-remaining-benefit-summary
              >
                <p class="tio-text-xs-medium app-status-text-brand uppercase tracking-wider">
                  Remaining Budget
                </p>
                <div class="space-y-1.5">
                  {{#unless Summary.isUnlimitedRemainingBenefit}}
                    <dl class="grid grid-cols-[1fr_auto] gap-y-2 items-center">
                      <Summary.MaxAnnualBenefitLineItem
                        @labelClass="text-sm tio-fg-secondary"
                        @valueClass="tio-text-sm-semibold tio-fg-primary"
                      />
                      <Summary.BenefitPaidForYearLineItem
                        @labelClass="text-sm tio-fg-secondary"
                        @valueClass="tio-text-sm-semibold tio-fg-primary"
                      />
                    </dl>
                  {{/unless}}
                  <dl
                    class="grid grid-cols-[1fr_auto] gap-y-2 items-center border-t app-divider-brand pt-1.5"
                  >
                    {{#if Summary.isUnlimitedRemainingBenefit}}
                      <dt class="tio-text-sm-medium app-status-text-brand">Remaining Benefit</dt>
                      <dd class="tio-text-base-bold app-status-text-brand text-right">Unlimited</dd>
                    {{else}}
                      <Summary.RemainingBenefitLineItem
                        @labelClass="tio-text-sm-medium app-status-text-brand"
                        @valueClass="tio-text-base-bold app-status-text-brand"
                      />
                    {{/if}}
                  </dl>
                </div>
              </div>
            {{/unless}}

            {{! Waiver Checklist Section - Must be completed first }}
            {{#if this.canSkipFinalApprovalOrAttachmentsWithChecklist}}
              <div class="space-y-3">
                <h3 class="tio-text-sm-medium">Bypass evidence approval step</h3>
                <TuitionAssistanceApplicationDetailsCoursesApprovalWaiverChecklist
                  @programTemplate={{@application.tasProgramInstance.tasProgramTemplate}}
                  @didChange={{this.didChangeApprovalWaiverChecklist}}
                />
              </div>
            {{/if}}

            {{! Approved Amount Section }}
            {{#if this.isPostPaymentReview}}
              <div class="rounded-md tio-bg-sunken p-4 space-y-2">
                <div class="tio-row-between">
                  <span class="text-sm tio-fg-muted">{{t
                      "common.tas.approval.post_payment_review.paid_amount"
                    }}</span>
                  <span class="tio-text-sm-semibold tio-fg-primary">{{safeFormatNumber
                      (divide (or @application.amounts.paid 0) 100)
                      style="currency"
                      currency="USD"
                    }}</span>
                </div>
                <p class="text-xs tio-fg-disabled">{{t
                    "common.tas.approval.post_payment_review.payment_processed"
                  }}</p>
              </div>
            {{else}}
              <div class="space-y-4">
                {{! Read-only breakdown of what was requested, so the approver can confirm
                the amount to enter without dismissing the modal to read the page behind it }}
                {{#if this.requestedExpenseRows.length}}
                  <div
                    class="rounded-md tio-bg-sunken p-4 space-y-2"
                    data-test-requested-expenses-summary
                  >
                    <p class="tio-text-xs-medium tio-fg-muted uppercase tracking-wider">
                      {{t "common.tas.approval.approve_modal.requested_expenses.heading"}}
                    </p>
                    <dl class="grid grid-cols-[1fr_auto] gap-y-1.5 items-center">
                      {{#each this.requestedExpenseRows as |row|}}
                        <dt class="text-sm tio-fg-secondary">{{row.label}}</dt>
                        <dd class="text-sm tio-fg-primary text-right">{{safeFormatNumber
                            (divide row.amount 100)
                            style="currency"
                            currency="USD"
                          }}</dd>
                      {{/each}}
                      <dt
                        class="tio-text-sm-medium tio-fg-primary border-t tio-border-default pt-1.5"
                      >{{t "common.tas.approval.approve_modal.requested_expenses.total"}}</dt>
                      <dd
                        class="tio-text-sm-semibold tio-fg-primary text-right border-t tio-border-default pt-1.5"
                        data-test-requested-expenses-total
                      >{{safeFormatNumber
                          (divide this.requestedExpensesTotal 100)
                          style="currency"
                          currency="USD"
                        }}</dd>
                    </dl>
                  </div>
                {{/if}}

                {{#if this.isCalculatedApplicationTotal}}
                  {{! Percentage-based calculation UI }}
                  {{#if this.showCourseCashField}}
                    {{! Course Cash with percentage }}
                    <div class="grid grid-cols-2 gap-2 items-center">
                      <label class="font-semibold">
                        {{this.reimbursementPercentage}}% reimbursement of
                      </label>
                      <Input
                        @value={{this.prePercentageCourseCashTotalInputValue}}
                        @onChange={{this.updateCourseCashFromPrePercentage}}
                        {{inputmask
                          alias="currency"
                          prefix="$"
                          unmaskAsNumber=true
                          digits="2"
                          autoUnmask=true
                        }}
                        @description="Course Cash - Tuition"
                        data-test-pre-percentage-cc-total
                      />

                      <label class="font-semibold">{{t
                          "common.tas.approval.approve_modal.approved_course_cash_total"
                        }}</label>
                      <Input
                        @value={{String (divide this.cardFinalApprovedTotal 100)}}
                        @isDisabled={{true}}
                        {{inputmask
                          alias="currency"
                          prefix="$"
                          unmaskAsNumber=true
                          digits="2"
                          autoUnmask=true
                        }}
                        data-test-card-final-approved-total-input
                      />
                    </div>

                    <hr class="my-2" />

                    <div class="grid grid-cols-2 gap-2 items-center">
                      <label class="font-semibold">
                        {{this.reimbursementPercentage}}% reimbursement of
                      </label>
                      <Input
                        @value={{this.prePercentageTotalInputValue}}
                        @onChange={{this.updateApprovedTotalFromPrePercentage}}
                        {{inputmask
                          alias="currency"
                          prefix="$"
                          unmaskAsNumber=true
                          digits="2"
                          autoUnmask=true
                        }}
                        @description="All other expenses"
                        data-test-pre-percentage-total
                      />

                      <label class="font-semibold">{{t
                          "common.tas.approval.approve_modal.approved_reimbursement_total"
                        }}</label>
                      <Input
                        @value={{String (divide this.approvedTotal 100)}}
                        @onChange={{this.updateApprovedTotal}}
                        @isDisabled={{true}}
                        {{inputmask
                          alias="currency"
                          prefix="$"
                          unmaskAsNumber=true
                          digits="2"
                          autoUnmask=true
                        }}
                        data-test-approved-total-input
                      />
                    </div>
                  {{else}}
                    {{! Non-Course Cash with percentage }}
                    <div class="grid grid-cols-2 gap-2 items-center">
                      <label class="font-semibold">
                        {{this.reimbursementPercentage}}% reimbursement of
                      </label>
                      <Input
                        @value={{this.prePercentageTotalInputValue}}
                        @onChange={{this.updateApprovedTotalFromPrePercentage}}
                        {{inputmask
                          alias="currency"
                          prefix="$"
                          unmaskAsNumber=true
                          digits="2"
                          autoUnmask=true
                        }}
                        data-test-pre-percentage-total
                      />

                      <label class="font-semibold">Approved Total</label>
                      <Input
                        @value={{String (divide this.approvedTotal 100)}}
                        @onChange={{this.updateApprovedTotal}}
                        @isDisabled={{true}}
                        {{inputmask
                          alias="currency"
                          prefix="$"
                          unmaskAsNumber=true
                          digits="2"
                          autoUnmask=true
                        }}
                        data-test-approved-total-input
                      />
                    </div>
                  {{/if}}
                {{else}}
                  {{! Direct input (non-percentage) UI }}
                  {{#if this.showCourseCashField}}
                    <Input
                      @label="Course Cash Amount"
                      @value={{this.cardFinalApprovedTotalInputValue}}
                      @onChange={{this.updateCardFinalApprovedTotal}}
                      {{inputmask
                        alias="currency"
                        prefix="$"
                        unmaskAsNumber=true
                        digits="2"
                        autoUnmask=true
                      }}
                      @description="Course Cash - Tuition"
                      data-test-card-final-approved-total-input
                    />
                  {{/if}}

                  {{#if this.showCourseCashField}}
                    <Input
                      @label="All Other Expenses"
                      @value={{this.approvedTotalInputValue}}
                      @onChange={{this.updateApprovedTotal}}
                      {{inputmask
                        alias="currency"
                        prefix="$"
                        unmaskAsNumber=true
                        digits="2"
                        autoUnmask=true
                      }}
                      data-test-approved-total-input
                    />
                  {{else}}
                    {{#if this.isCreditBased}}
                      <div class="space-y-4">
                        <div class="tio-row-between py-2 border-b tio-border-default">
                          <span class="tio-text-sm-medium tio-fg-secondary">Total Credits</span>
                          <span
                            class="tio-text-sm-semibold tio-fg-primary"
                          >{{@application.totalCredits}} credits</span>
                        </div>
                        <Input
                          @value={{this.approvedTotalInputValue}}
                          @label={{t
                            "common.tas.approval.approve_modal.approved_total_for_reimbursement"
                          }}
                          @onChange={{this.updateApprovedTotal}}
                          {{inputmask
                            alias="currency"
                            prefix="$"
                            unmaskAsNumber=true
                            digits="2"
                            autoUnmask=true
                          }}
                          data-test-approved-total-input
                        />
                      </div>
                    {{else}}
                      <Input
                        @value={{this.approvedTotalInputValue}}
                        @label={{t
                          "common.tas.approval.approve_modal.approved_total_for_reimbursement"
                        }}
                        @onChange={{this.updateApprovedTotal}}
                        {{inputmask
                          alias="currency"
                          prefix="$"
                          unmaskAsNumber=true
                          digits="2"
                          autoUnmask=true
                        }}
                        data-test-approved-total-input
                      />
                    {{/if}}
                  {{/if}}
                {{/if}}
              </div>
            {{/if}}

            {{! Comment Section }}
            <div class="space-y-2">
              <h3 class="tio-text-sm-medium">Leave a Comment</h3>
              {{#if this.approveTextAreaOptions.length}}
                <SelectTextArea
                  @options={{this.approveTextAreaOptions}}
                  @optionLabel="title"
                  @optionValue="body"
                  @value={{this.comment}}
                  @onChange={{this.updateComment}}
                  @label="Select a response option or write your own"
                  @hideLabel={{true}}
                  @selectPlaceholder="Select a response..."
                  @placeholder="Or write your own comment..."
                  @multiSelect={{true}}
                />
              {{else}}
                <Textarea
                  @value={{this.comment}}
                  @onChange={{this.updateComment}}
                  data-test-approve-comment
                />
              {{/if}}
            </div>

            {{! Warnings Section }}
            {{#if this.employeeHasAppsInRepayment}}
              <ErrorMessages
                @showErrors={{true}}
                @error="Please keep in mind that the employee has applications in repayment."
                class="text-sm"
              />
            {{/if}}

            {{#if this.isTasParticipantInactive}}
              <ErrorMessages
                @showErrors={{true}}
                @error="This employee is inactive in the TAS program."
                class="text-sm"
              />
            {{/if}}
          </VStack>

          {{! Out-of-sequence approver confirmation }}
          <ConfirmationDialog
            @isOpen={{this.showConfirmApproverOverridePrompt}}
            @title="Out of Sequence Approver"
            @intent="warning"
            @onConfirm={{this.submitApprovalTask.perform}}
            @onCancel={{this.cancelApproverOverride}}
            @confirmText="Yes"
            @cancelText="No"
          >
            <p class="mb-2 font-semibold">
              You are not the expected approver for this application. Are you sure you want to
              proceed with this approval?
            </p>
          </ConfirmationDialog>
        </:body>
        <:footer>
          {{#if (or this.hasValidationBlockers Summary.isDisabledSubmitForApprover)}}
            <div class="text-xs tio-fg-danger mb-3">
              <p class="font-medium">{{t
                  "common.tas.approval.approve_modal.validation_blockers.header"
                }}</p>
              <ul class="mt-0.5 list-disc pl-4 space-y-0.5">
                {{#each this.validationBlockersForDisplay as |reason|}}
                  <li>{{reason}}</li>
                {{/each}}
                {{#if Summary.isDisabledSubmitForApprover}}
                  <li>{{t
                      "common.tas.approval.approve_modal.validation_blockers.limits_exceeded"
                    }}</li>
                {{/if}}
              </ul>
            </div>
          {{/if}}
          <Footer
            @isRunning={{this.submitApprovalTask.isRunning}}
            @submitText="Approve"
            @cancelText="Cancel"
            @onSubmit={{this.attemptSubmitApproval}}
            @onCancel={{this.handleClose}}
            @submitDisabled={{or (not this.canSubmitApproval) Summary.isDisabledSubmitForApprover}}
          />
        </:footer>

        {{! Approver instructions, beside the modal rather than inside it — the
        approver reads them while filling in the totals, and the Employer Info
        drawer that otherwise carries them sits behind this modal. }}
        <:aside>
          {{! pl-20 = the -ml-12 tuck (48px) plus a 32px gutter, so the text sits clear
          of the modal edge rather than hugging it }}
          {{! No close button here — the header toggle is the drawer's only
          control, so there is one obvious way to open and shut it. }}
          <div class="flex-none pl-20 pr-4 py-3 border-b tio-border-default">
            <h3 class="tio-text-sm-semibold tio-fg-primary">
              {{t "tuition_assistance.application_approval.approver_instructions"}}
            </h3>
          </div>
          <div
            class="flex-1 min-h-0 overflow-y-auto pl-20 pr-4 py-3"
            data-test-approve-modal-instructions
          >
            <ul class="list-disc ml-4 space-y-3 tio-fg-secondary text-sm leading-relaxed">
              {{#each this.approverInstructions as |item|}}
                <li>
                  {{#if item.heading}}
                    <span class="tio-text-sm-semibold tio-fg-primary block">{{item.heading}}</span>
                  {{/if}}
                  {{htmlSafe item.body}}
                </li>
              {{/each}}
            </ul>
          </div>
        </:aside>
      </Modal>
    </LimitsSummary>
  </template>
}

export default ApproveModal;
